<template>
  <v-container class="certificate-import py-7">
    <div class="certificate-import__header">
      <div>
        <v-breadcrumbs class="app-breadcrumb pa-0 mb-2" divider="›" :items="breadcrumbs" />
        <h1 class="certificate-import__title">Importação em lote</h1>
        <p class="certificate-import__subtitle">Envie até 500 certidões de uma vez usando o modelo oficial.</p>
      </div>
      <v-btn outlined color="primary" to="/certidoes-habilitacao">
        <v-icon left>mdi-arrow-left</v-icon>Voltar às certidões
      </v-btn>
    </div>

    <v-card outlined class="certificate-import__stepper mb-5">
      <div v-for="(step, index) in steps" :key="step.label" class="certificate-import__step">
        <div class="certificate-import__step-mark" :class="{
          'certificate-import__step-mark--active': index === currentStep,
          'certificate-import__step-mark--done': index < currentStep
        }">
          <v-icon v-if="index < currentStep" small color="white">mdi-check</v-icon>
          <span v-else>{{ index + 1 }}</span>
        </div>
        <span :class="{ 'primary--text font-weight-bold': index === currentStep }">{{ step.label }}</span>
        <div v-if="index < steps.length - 1" class="certificate-import__step-line"
          :class="{ 'certificate-import__step-line--done': index < currentStep }" />
      </div>
    </v-card>

    <v-row v-if="!currentImport">
      <v-col cols="12" lg="8">
        <v-card outlined class="certificate-import__card">
          <v-card-title class="certificate-import__card-title">Modelo e planilha preenchida</v-card-title>
          <v-card-text>
            <div class="certificate-import__template">
              <div class="d-flex align-center">
                <v-avatar color="#EEF1FB" size="48" class="mr-4">
                  <v-icon color="primary">mdi-file-excel-outline</v-icon>
                </v-avatar>
                <div>
                  <div class="font-weight-bold">Modelo oficial de importação</div>
                  <div class="certificate-import__muted">Já contém os nomes das colunas e uma linha de exemplo.</div>
                </div>
              </div>
              <v-btn color="primary" outlined :loading="downloadingTemplate" @click="downloadTemplate">
                <v-icon left>mdi-download</v-icon>Baixar modelo
              </v-btn>
            </div>

            <div class="certificate-import__dropzone mt-5" :class="{ 'certificate-import__dropzone--selected': spreadsheet }">
              <div class="certificate-import__dropzone-content">
                <v-icon v-if="spreadsheet" size="42" color="primary">mdi-file-check-outline</v-icon>
                <v-icon v-else size="42" color="primary">mdi-cloud-upload-outline</v-icon>
                <div v-if="spreadsheet" class="font-weight-bold mt-3">{{ spreadsheet.name }}</div>
                <div v-else class="font-weight-bold mt-3">Arraste a planilha aqui ou clique para selecionar</div>
                <div class="certificate-import__muted mt-1">
                  {{ spreadsheet ? "Planilha pronta para validação" : "Arquivo XLSX ou XLS, com até 500 certidões" }}
                </div>
              </div>
              <v-file-input class="certificate-import__file-input" accept=".xlsx,.xls"
                prepend-icon="" hide-details label="Selecionar planilha"
                v-model="spreadsheet" :disabled="uploading" />
            </div>
          </v-card-text>
          <v-card-actions class="px-6 pb-6">
            <v-spacer />
            <v-btn color="primary" elevation="0" :loading="uploading" :disabled="!spreadsheet"
              @click="uploadSpreadsheet">
              Validar planilha<v-icon right>mdi-arrow-right</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>

      <v-col cols="12" lg="4">
        <v-card outlined class="certificate-import__card">
          <v-card-title class="certificate-import__card-title">
            <v-icon color="primary" left>mdi-information-outline</v-icon>Antes de começar
          </v-card-title>
          <v-card-text>
            <div v-for="instruction in instructions" :key="instruction.title" class="certificate-import__instruction">
              <v-icon color="success" small class="mr-3 mt-1">mdi-check-circle</v-icon>
              <div>
                <strong>{{ instruction.title }}</strong>
                <div class="certificate-import__muted">{{ instruction.text }}</div>
              </div>
            </div>
            <v-alert color="#FFF7E8" border="left" colored-border class="mt-4 mb-0">
              Os documentos precisam estar no Google Drive com acesso
              <strong>“Qualquer pessoa com o link”</strong>.
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card v-if="!currentImport" outlined class="certificate-import__card certificate-import__guide mb-5">
      <v-card-title class="certificate-import__card-title">
        <v-icon color="primary" left>mdi-book-open-page-variant-outline</v-icon>
        Como preparar a planilha
      </v-card-title>
      <v-card-text>
        <p class="certificate-import__guide-intro">
          Baixe o modelo oficial e preencha somente com as informações do processo e da certidão.
          Os nomes apresentados abaixo são os mesmos encontrados na planilha.
        </p>

        <div class="certificate-import__guide-steps">
          <div v-for="(item, index) in preparationSteps" :key="item.title">
            <span>{{ index + 1 }}</span>
            <div><strong>{{ item.title }}</strong><small>{{ item.text }}</small></div>
          </div>
        </div>

        <v-expansion-panels accordion flat class="certificate-import__guide-panels">
          <v-expansion-panel v-for="sheet in spreadsheetSheets" :key="sheet.name">
            <v-expansion-panel-header>
              <div>
                <strong>Aba {{ sheet.name }}</strong>
                <small>{{ sheet.description }}</small>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="certificate-import__columns">
                <div v-for="column in sheet.columns" :key="column.name">
                  <div>
                    <strong>{{ column.name }}</strong>
                    <v-chip x-small label :color="column.required ? '#E8ECFB' : '#F2F4F7'"
                      :text-color="column.required ? '#2A3070' : '#667085'">
                      {{ column.required ? "Obrigatório" : "Opcional" }}
                    </v-chip>
                  </div>
                  <p>{{ column.description }}</p>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
    </v-card>

    <v-card v-if="currentImport" outlined class="certificate-import__card mb-5">
      <v-card-title class="certificate-import__review-header">
        <div>
          <div class="certificate-import__card-title pa-0">Conferência da importação</div>
          <div class="certificate-import__muted mt-1">{{ currentImport.source_file_name }}</div>
        </div>
        <v-chip small :color="statusColor(currentImport.status)" dark>{{ statusLabel(currentImport.status) }}</v-chip>
      </v-card-title>

      <v-card-text>
        <div class="certificate-import__summary">
          <div v-for="summary in importSummary" :key="summary.label" class="certificate-import__summary-item">
            <span class="certificate-import__muted">{{ summary.label }}</span>
            <strong :class="summary.color + '--text'">{{ summary.value }}</strong>
          </div>
        </div>

        <v-tabs v-model="reviewTab" background-color="transparent" color="primary" class="mt-5">
          <v-tab href="#invalid">Inválidos ({{ invalidItems.length }})</v-tab>
          <v-tab href="#valid">Válidos ({{ validItems.length }})</v-tab>
        </v-tabs>

        <v-data-table :headers="headers" :items="reviewItems" :items-per-page="10"
          class="app-table certificate-import__table" no-data-text="Nenhum registro nesta situação">
          <template v-slot:[`item.creditor`]="{ item }">
            <strong>{{ item.reference }}</strong>
          </template>
          <template v-slot:[`item.amount`]="{ item }">
            {{ (item.normalized_payload.creditors || []).length }}
          </template>
          <template v-slot:[`item.documents`]="{ item }">
            {{ itemDocumentCount(item) }} {{ itemDocumentCount(item) === 1 ? "documento" : "documentos" }}
          </template>
          <template v-slot:[`item.status`]="{ item }">
            <v-chip x-small :color="statusColor(item.status)" dark>{{ statusLabel(item.status) }}</v-chip>
          </template>
          <template v-slot:[`item.errors`]="{ item }">
            <span :class="item.status === 'duplicate' ? 'warning--text' : 'error--text'">
              {{ itemError(item) || itemStatusDescription(item) }}
            </span>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn v-if="item.status === 'failed'" x-small outlined color="primary"
              :loading="retryingItemId === item.id" @click="retryItem(item)">Tentar novamente</v-btn>
          </template>
        </v-data-table>

      </v-card-text>

      <v-progress-linear v-if="processing || currentImport.status === 'processing'"
        :value="progressPercent" height="10" color="primary" />

      <v-card-actions class="px-6 pb-6">
        <v-btn text color="primary" @click="resetImport">Enviar outra planilha</v-btn>
        <v-btn v-if="canDownloadReport" outlined color="primary" @click="downloadReport">
          <v-icon left>mdi-file-excel-outline</v-icon>Baixar resultado
        </v-btn>
        <v-spacer />
        <v-btn v-if="currentImport.status === 'awaiting_confirmation'" color="primary" elevation="0"
          :loading="confirming" @click="confirmImport">
          Cadastrar {{ confirmableItems }} certidões
        </v-btn>
        <v-btn v-if="currentImport.status === 'processing'" color="primary" elevation="0"
          :loading="processing" @click="startProcessing">
          {{ processing ? "Processando..." : "Continuar processamento" }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-card outlined class="certificate-import__card">
      <v-card-title class="certificate-import__card-title">Importações anteriores</v-card-title>
      <v-card-text>
        <v-data-table :headers="historyHeaders" :items="imports" :loading="loadingImports"
          :items-per-page="10" class="app-table" no-data-text="Nenhuma importação encontrada">
          <template v-slot:[`item.status`]="{ item }">
            <v-chip x-small :color="statusColor(item.status)" dark>{{ statusLabel(item.status) }}</v-chip>
          </template>
          <template v-slot:[`item.progress`]="{ item }">
            {{ Number(item.processed_items || 0) + Number(item.inconsistent_items || 0) }} de {{ item.total_items }}
          </template>
          <template v-slot:[`item.created_at`]="{ item }">
            {{ item.created_at | defaultDate("DD/MM/YYYY HH:mm") }}
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn x-small outlined color="primary" @click="openImport(item.id)">Consultar</v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import axios from "axios";

export default {
  name: "CreditCertificateImport",
  data: () => ({
    breadcrumbs: [
      { text: "Certidões de Habilitação de Crédito", to: "/certidoes-habilitacao", exact: true },
      { text: "Importação em lote", disabled: true },
    ],
    spreadsheet: null,
    uploading: false,
    downloadingTemplate: false,
    confirming: false,
    processing: false,
    retryingItemId: null,
    confirmDuplicates: false,
    reviewTab: "invalid",
    currentImport: null,
    imports: [],
    loadingImports: false,
    steps: [
      { label: "Modelo e planilha" },
      { label: "Validação" },
      { label: "Conferência" },
      { label: "Processamento" },
    ],
    instructions: [
      { title: "Um identificador por certidão", text: "Crie um identificador simples para cada certidão da planilha." },
      { title: "Preenchimento em português", text: "Todos os campos usam nomes e opções de negócio, sem códigos do sistema." },
      { title: "Abas relacionadas", text: "Repita o mesmo identificador nas abas de credores, créditos e honorários." },
      { title: "Conferência antes de importar", text: "Inválidos e possíveis duplicidades serão destacados." },
    ],
    preparationSteps: [
      { title: "Baixe o modelo oficial", text: "Não altere os nomes das abas nem das colunas." },
      { title: "Crie um identificador", text: "Use um código simples, como CHC-001, para relacionar as informações." },
      { title: "Preencha as abas necessárias", text: "Repita o identificador sempre que a certidão tiver credores, créditos ou honorários." },
      { title: "Revise e envie", text: "Remova a linha de exemplo e confira os links antes da importação." },
    ],
    spreadsheetSheets: [
      {
        name: "Certidões",
        description: "Dados do processo e links dos documentos.",
        columns: [
          { name: "Identificador da certidão", required: true, description: "Código criado por você para relacionar esta certidão às demais abas. Exemplo: CHC-001." },
          { name: "Processo de origem", required: true, description: "Número completo do processo no padrão CNJ." },
          { name: "Estado do processo", required: true, description: "Sigla do estado onde tramita o processo, como MG ou SP." },
          { name: "Ramo da Justiça", required: true, description: "Exemplo: Justiça Estadual, Justiça Federal ou Justiça do Trabalho." },
          { name: "Órgão julgador", required: true, description: "Vara, juízo ou órgão responsável pelo processo." },
          { name: "Datas do processo e dos cálculos", required: true, description: "Informe as datas no formato dia/mês/ano." },
          { name: "Processo sigiloso", required: true, description: "Preencha Sim ou Não." },
          { name: "Link público da certidão", required: true, description: "Link de compartilhamento do arquivo no Google Drive." },
          { name: "Demais documentos", required: false, description: "Decisão homologatória, planilha de cálculos, processo integral e observações." },
        ],
      },
      {
        name: "Credores",
        description: "Pessoas ou empresas relacionadas a cada certidão.",
        columns: [
          { name: "Identificador da certidão", required: true, description: "Repita o mesmo identificador usado na aba Certidões." },
          { name: "Nome do credor", required: true, description: "Nome completo ou razão social." },
          { name: "CPF/CNPJ do credor", required: true, description: "Informe somente um documento válido por linha." },
        ],
      },
      {
        name: "Créditos",
        description: "Valores e classificação dos créditos.",
        columns: [
          { name: "Identificador da certidão", required: true, description: "Repita o identificador correspondente da aba Certidões." },
          { name: "CPF/CNPJ do credor", required: true, description: "Documento do credor ao qual o valor pertence." },
          { name: "Moeda e valor do crédito", required: true, description: "Use BRL (R$), USD ($) ou EUR (€) e informe o valor." },
          { name: "Origem e classe do crédito", required: true, description: "Selecione as opções descritas na aba Instruções do modelo." },
        ],
      },
      {
        name: "Honorários",
        description: "Preencha somente quando houver honorários.",
        columns: [
          { name: "Identificador da certidão", required: true, description: "Repita o identificador correspondente da aba Certidões." },
          { name: "Tipo de honorário", required: true, description: "Preencha Advocatícios ou Periciais." },
          { name: "Dados do beneficiário", required: true, description: "Nome, CPF/CNPJ, valor e data de fixação." },
          { name: "OAB e estado da OAB", required: false, description: "Obrigatórios somente para honorários advocatícios." },
          { name: "Link do documento", required: false, description: "Link público do documento no Google Drive." },
        ],
      },
    ],
    headers: [
      { text: "Linha", value: "row_number" },
      { text: "Referência", value: "creditor" },
      { text: "Credores", value: "amount" },
      { text: "Documentos", value: "documents" },
      { text: "Status", value: "status" },
      { text: "Erros", value: "errors" },
      { text: "Ações", value: "actions", sortable: false },
    ],
    historyHeaders: [
      { text: "Arquivo", value: "source_file_name" },
      { text: "Status", value: "status" },
      { text: "Total", value: "total_items" },
      { text: "Progresso", value: "progress" },
      { text: "Criado em", value: "created_at" },
      { text: "Ações", value: "actions", sortable: false },
    ],
  }),
  computed: {
    currentStep() {
      if (!this.currentImport) return this.uploading ? 1 : 0;
      if (this.currentImport.status === "awaiting_confirmation") return 2;
      if (["processing", "completed", "completed_with_errors"].includes(this.currentImport.status)) return 3;
      return 1;
    },
    invalidItems() {
      return this.currentImport && this.currentImport.items
        ? this.currentImport.items.filter((item) => ["invalid", "failed"].includes(item.status))
        : [];
    },
    validItems() {
      return this.currentImport && this.currentImport.items
        ? this.currentImport.items.filter((item) =>
          ["ready", "processed", "inconsistent", "processing"].includes(item.status))
        : [];
    },
    reviewItems() {
      if (this.reviewTab === "valid") return this.validItems;
      return this.invalidItems;
    },
    confirmableItems() {
      return this.validItems.filter((item) => item.status === "ready").length;
    },
    importSummary() {
      if (!this.currentImport) return [];
      return [
        { label: "Total", value: this.currentImport.total_items || 0, color: "primary" },
        { label: "Válidos", value: this.validItems.length, color: "success" },
        { label: "Inválidos", value: this.invalidItems.length, color: "error" },
        {
          label: "Processados",
          value: Number(this.currentImport.processed_items || 0) +
            Number(this.currentImport.inconsistent_items || 0),
          color: "primary",
        },
      ];
    },
    progressPercent() {
      if (!this.currentImport || !this.currentImport.valid_items) return 0;
      if (["completed", "completed_with_errors"].includes(this.currentImport.status)) return 100;
      const complete = Number(this.currentImport.processed_items || 0) +
        Number(this.currentImport.inconsistent_items || 0) +
        Number(this.currentImport.failed_items || 0);
      return Math.min(100, (complete / Number(this.currentImport.valid_items)) * 100);
    },
    canDownloadReport() {
      return this.currentImport &&
        ["completed", "completed_with_errors"].includes(this.currentImport.status);
    },
  },
  async created() {
    await this.loadImports();
    if (this.$route.query.id) await this.openImport(this.$route.query.id);
  },
  methods: {
    saveBlob(response, name) {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    async downloadTemplate() {
      this.downloadingTemplate = true;
      try {
        const response = await axios.get("/api/client/credit-certificate-imports/template", { responseType: "blob" });
        this.saveBlob(response, "modelo-importacao-chc.xlsx");
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right" });
      } finally {
        this.downloadingTemplate = false;
      }
    },
    async uploadSpreadsheet() {
      this.uploading = true;
      try {
        const payload = new FormData();
        payload.append("file", this.spreadsheet);
        const response = await axios.post("/api/client/credit-certificate-imports", payload);
        this.currentImport = response.data.content;
        this.confirmDuplicates = false;
        this.reviewTab = this.invalidItems.length ? "invalid" : "valid";
        await this.loadImports();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 4000 });
      } finally {
        this.uploading = false;
      }
    },
    async loadImports() {
      this.loadingImports = true;
      try {
        const response = await axios.get("/api/client/credit-certificate-imports", { params: { page: 1, limit: 50 } });
        const content = response.data.content || {};
        this.imports = content.data || [];
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right" });
      } finally {
        this.loadingImports = false;
      }
    },
    async openImport(id) {
      try {
        const response = await axios.get(`/api/client/credit-certificate-imports/${id}`);
        this.currentImport = response.data.content;
        this.confirmDuplicates = false;
        this.reviewTab = this.invalidItems.length ? "invalid" : "valid";
        if (this.currentImport.status === "processing") await this.startProcessing();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right" });
      }
    },
    async confirmImport() {
      this.confirming = true;
      try {
        const response = await axios.post(`/api/client/credit-certificate-imports/${this.currentImport.id}/confirm`);
        this.currentImport = response.data.content;
        await this.startProcessing();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right" });
      } finally {
        this.confirming = false;
      }
    },
    async processingWorker() {
      while (this.processing) {
        const response = await axios.post(`/api/client/credit-certificate-imports/${this.currentImport.id}/process-next`);
        const content = response.data.content;
        if (content.progress) {
          this.currentImport = { ...this.currentImport, ...content.progress };
        }
        if (!content.has_more) break;
      }
    },
    async startProcessing() {
      if (this.processing || !this.currentImport) return;
      this.processing = true;
      try {
        await Promise.all([this.processingWorker(), this.processingWorker(), this.processingWorker()]);
        await this.openImportWithoutProcessing(this.currentImport.id);
        await this.loadImports();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 4000 });
      } finally {
        this.processing = false;
      }
    },
    async openImportWithoutProcessing(id) {
      const response = await axios.get(`/api/client/credit-certificate-imports/${id}`);
      this.currentImport = response.data.content;
    },
    async retryItem(item) {
      this.retryingItemId = item.id;
      try {
        const replacement = window.prompt(
          "Se o link da CHC estiver inválido ou expirado, informe o novo link público do Google Drive. Deixe em branco para repetir com o mesmo link.",
          ""
        );
        if (replacement === null) return;
        const response = await axios.post(
          `/api/client/credit-certificate-imports/${this.currentImport.id}/items/${item.id}/retry`,
          replacement ? { document_links: { chc: replacement } } : {}
        );
        this.currentImport = response.data.content;
        if (this.currentImport.status === "processing") await this.startProcessing();
        else await this.loadImports();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right" });
      } finally {
        this.retryingItemId = null;
      }
    },
    async downloadReport() {
      try {
        const response = await axios.get(
          `/api/client/credit-certificate-imports/${this.currentImport.id}/report`,
          { responseType: "blob" }
        );
        this.saveBlob(response, `resultado-importacao-${this.currentImport.id}.xlsx`);
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right" });
      }
    },
    itemError(item) {
      const validation = item.validation_errors || [];
      return [...validation, item.error_message].filter(Boolean).join(" | ");
    },
    itemStatusDescription(item) {
      return "";
    },
    itemDocumentCount(item) {
      const links = item.document_links || {};
      const simple = ["chc", "homologation", "spreadsheet", "confidential"]
        .filter((type) => !!links[type]).length;
      return simple + (links.attorneyFees || []).filter(Boolean).length +
        (links.expertFees || []).filter(Boolean).length;
    },
    resetImport() {
      this.currentImport = null;
      this.spreadsheet = null;
      this.confirmDuplicates = false;
      this.reviewTab = "invalid";
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
    formatRegistration(value) {
      const digits = String(value || "").replace(/\D/g, "");
      if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      return value || "-";
    },
    statusLabel(status) {
      return {
        validating: "Validando",
        awaiting_confirmation: "Aguardando confirmação",
        processing: "Processando",
        completed: "Concluído",
        completed_with_errors: "Concluído com erros",
        invalid: "Inválido",
        ready: "Pronto",
        processed: "Processado",
        inconsistent: "Com inconsistência",
        failed: "Falhou",
        duplicate: "Possível duplicidade",
      }[status] || status;
    },
    statusColor(status) {
      if (["completed", "processed"].includes(status)) return "success";
      if (["failed", "invalid"].includes(status)) return "error";
      if (["completed_with_errors", "inconsistent", "duplicate"].includes(status)) return "warning";
      if (["processing", "validating"].includes(status)) return "info";
      return "primary";
    },
  },
};
</script>

<style scoped>
.certificate-import {
  max-width: 1440px;
}

.certificate-import__header,
.certificate-import__template,
.certificate-import__review-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.certificate-import__header {
  margin-bottom: 24px;
}

.certificate-import__title {
  color: #101828;
  font-size: 28px;
  line-height: 1.25;
}

.certificate-import__subtitle,
.certificate-import__muted {
  color: #667085;
}

.certificate-import__subtitle {
  margin: 6px 0 0;
}

.certificate-import__card,
.certificate-import__stepper {
  border-color: #e4e7ec !important;
  border-radius: 10px;
}

.certificate-import__card-title {
  color: #101828;
  font-size: 18px;
  font-weight: 600;
}

.certificate-import__stepper {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  padding: 20px 24px;
}

.certificate-import__step {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
  color: #667085;
}

.certificate-import__step-mark {
  z-index: 1;
  display: flex;
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  align-items: center;
  justify-content: center;
  border: 1px solid #d0d5dd;
  border-radius: 50%;
  background: #fff;
  color: #98a2b3;
}

.certificate-import__step-mark--active {
  border-color: #2a3070;
  background: #eef1fb;
  color: #2a3070;
  font-weight: 700;
}

.certificate-import__step-mark--done {
  border-color: #2a3070;
  background: #2a3070;
}

.certificate-import__step-line {
  flex: 1 1 auto;
  min-width: 16px;
  height: 1px;
  background: #e4e7ec;
}

.certificate-import__step-line--done {
  background: #2a3070;
}

.certificate-import__dropzone {
  position: relative;
  display: flex;
  min-height: 190px;
  align-items: center;
  justify-content: center;
  padding: 24px;
  border: 1.5px dashed #98a2b3;
  border-radius: 10px;
  background: #fafbfc;
}

.certificate-import__dropzone-content {
  position: relative;
  z-index: 1;
  text-align: center;
  pointer-events: none;
}

.certificate-import__dropzone--selected {
  border-color: #2a3070;
  background: #f7f8fc;
}

.certificate-import__file-input {
  position: absolute;
  z-index: 2;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: pointer;
}

.certificate-import__file-input ::v-deep .v-input__control,
.certificate-import__file-input ::v-deep .v-input__slot {
  height: 100%;
}

.certificate-import__file-input ::v-deep input {
  cursor: pointer;
}

.certificate-import__instruction {
  display: flex;
  padding: 12px 0;
  border-bottom: 1px solid #eef0f3;
}

.certificate-import__instruction:last-child {
  border-bottom: 0;
}

.certificate-import__guide-intro {
  max-width: 900px;
  margin: 0 0 20px;
  color: #667085;
  line-height: 1.55;
}

.certificate-import__guide-steps {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 22px;
}

.certificate-import__guide-steps > div {
  display: flex;
  gap: 12px;
  padding: 14px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #fafbfc;
}

.certificate-import__guide-steps span {
  display: grid;
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  place-items: center;
  border-radius: 50%;
  background: #eef1fb;
  color: #2a3070;
  font-weight: 700;
}

.certificate-import__guide-steps strong,
.certificate-import__guide-steps small,
.certificate-import__guide-panels small {
  display: block;
}

.certificate-import__guide-steps small,
.certificate-import__guide-panels small {
  margin-top: 3px;
  color: #667085;
}

.certificate-import__guide-panels {
  border: 1px solid #e4e7ec;
  border-radius: 8px;
}

.certificate-import__guide-panels ::v-deep .v-expansion-panel::before {
  box-shadow: none;
}

.certificate-import__guide-panels ::v-deep .v-expansion-panel + .v-expansion-panel {
  border-top: 1px solid #e4e7ec;
}

.certificate-import__columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  padding-bottom: 8px;
}

.certificate-import__columns > div {
  padding: 12px 14px;
  border: 1px solid #eaecf0;
  border-radius: 7px;
}

.certificate-import__columns > div > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.certificate-import__columns p {
  margin: 6px 0 0;
  color: #667085;
  font-size: 12px;
  line-height: 1.45;
}

.certificate-import__summary {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
}

.certificate-import__summary-item {
  display: flex;
  min-height: 76px;
  flex-direction: column;
  justify-content: center;
  padding: 12px 16px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
}

.certificate-import__summary-item strong {
  margin-top: 3px;
  font-size: 22px;
}

@media (max-width: 960px) {
  .certificate-import__header,
  .certificate-import__template,
  .certificate-import__review-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .certificate-import__stepper,
  .certificate-import__summary,
  .certificate-import__guide-steps {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }

  .certificate-import__step-line {
    display: none;
  }
}

@media (max-width: 600px) {
  .certificate-import__stepper,
  .certificate-import__summary,
  .certificate-import__guide-steps,
  .certificate-import__columns {
    grid-template-columns: 1fr;
  }
}
</style>
